/**
 * IndexedDB Service - Armazenamento local para modo offline
 * 
 * Collections:
 * - patients_local: Pacientes criados/editados offline
 * - prescriptions_local: Receitas em rascunho
 * - sync_queue: Fila de sincronização
 * - cached_data: Dados cacheados do servidor
 */

const DB_NAME = 'ReceitasOfflineDB';
const DB_VERSION = 1;

let db = null;

// Inicializar IndexedDB
export async function initDB() {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[IndexedDB] Error opening database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[IndexedDB] Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      console.log('[IndexedDB] Upgrading database...');

      // Store: patients_local
      if (!database.objectStoreNames.contains('patients_local')) {
        const patientsStore = database.createObjectStore('patients_local', { keyPath: 'local_id' });
        patientsStore.createIndex('server_id', 'server_id', { unique: false });
        patientsStore.createIndex('nome_normalizado', 'nome_normalizado', { unique: false });
        patientsStore.createIndex('sync_status', 'sync_status', { unique: false });
        patientsStore.createIndex('created_at', 'created_at', { unique: false });
      }

      // Store: prescriptions_local
      if (!database.objectStoreNames.contains('prescriptions_local')) {
        const prescStore = database.createObjectStore('prescriptions_local', { keyPath: 'local_id' });
        prescStore.createIndex('server_id', 'server_id', { unique: false });
        prescStore.createIndex('patient_local_id', 'patient_local_id', { unique: false });
        prescStore.createIndex('sync_status', 'sync_status', { unique: false });
        prescStore.createIndex('status', 'status', { unique: false });
      }

      // Store: sync_queue
      if (!database.objectStoreNames.contains('sync_queue')) {
        const syncStore = database.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('entity_type', 'entity_type', { unique: false });
        syncStore.createIndex('entity_local_id', 'entity_local_id', { unique: false });
        syncStore.createIndex('created_at', 'created_at', { unique: false });
      }

      // Store: cached_data (para dados do servidor)
      if (!database.objectStoreNames.contains('cached_data')) {
        const cacheStore = database.createObjectStore('cached_data', { keyPath: 'key' });
        cacheStore.createIndex('type', 'type', { unique: false });
        cacheStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // Store: app_state (estado do app)
      if (!database.objectStoreNames.contains('app_state')) {
        database.createObjectStore('app_state', { keyPath: 'key' });
      }

      console.log('[IndexedDB] Database schema created');
    };
  });
}

// Obter conexão do banco
async function getDB() {
  if (!db) {
    await initDB();
  }
  return db;
}

// ==================== PATIENTS ====================

// Normalizar nome (remover acentos, ç -> c, lowercase)
export function normalizeNameForDB(name) {
  if (!name) return '';
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ç/gi, 'c')
    .toLowerCase()
    .trim();
}

// Gerar UUID local
export function generateLocalId() {
  return 'local_' + crypto.randomUUID();
}

// Criar paciente local
export async function createPatientLocal(patientData) {
  const database = await getDB();
  
  const patient = {
    local_id: generateLocalId(),
    server_id: null,
    nome_original: patientData.nome,
    nome_normalizado: normalizeNameForDB(patientData.nome),
    data_nascimento: patientData.data_nascimento,
    telefone: patientData.telefone || null,
    cpf: patientData.cpf || null,
    cns: patientData.cns || null,
    unit_id: patientData.unit_id || null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    sync_status: 'local_only',
    version: 1
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['patients_local'], 'readwrite');
    const store = transaction.objectStore('patients_local');
    const request = store.add(patient);

    request.onsuccess = () => {
      console.log('[IndexedDB] Patient created locally:', patient.local_id);
      // Adicionar à fila de sync
      addToSyncQueue('patient', patient.local_id, 'create', patient);
      resolve(patient);
    };

    request.onerror = () => {
      console.error('[IndexedDB] Error creating patient:', request.error);
      reject(request.error);
    };
  });
}

// Atualizar paciente local
export async function updatePatientLocal(localId, updateData) {
  const database = await getDB();

  return new Promise(async (resolve, reject) => {
    const transaction = database.transaction(['patients_local'], 'readwrite');
    const store = transaction.objectStore('patients_local');
    
    const getRequest = store.get(localId);
    
    getRequest.onsuccess = () => {
      const patient = getRequest.result;
      if (!patient) {
        reject(new Error('Paciente não encontrado'));
        return;
      }

      const updatedPatient = {
        ...patient,
        ...updateData,
        nome_normalizado: updateData.nome ? normalizeNameForDB(updateData.nome) : patient.nome_normalizado,
        updated_at: new Date().toISOString(),
        sync_status: patient.server_id ? 'pending_sync' : 'local_only',
        version: patient.version + 1
      };

      const putRequest = store.put(updatedPatient);
      
      putRequest.onsuccess = () => {
        console.log('[IndexedDB] Patient updated locally:', localId);
        addToSyncQueue('patient', localId, 'update', updatedPatient);
        resolve(updatedPatient);
      };

      putRequest.onerror = () => {
        reject(putRequest.error);
      };
    };

    getRequest.onerror = () => {
      reject(getRequest.error);
    };
  });
}

// Buscar paciente local
export async function getPatientLocal(localId) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['patients_local'], 'readonly');
    const store = transaction.objectStore('patients_local');
    const request = store.get(localId);

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// Buscar todos os pacientes locais
export async function getAllPatientsLocal() {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['patients_local'], 'readonly');
    const store = transaction.objectStore('patients_local');
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// Verificar homônimo local
export async function checkHomonymLocal(nome, dataNascimento) {
  const database = await getDB();
  const nomeNormalizado = normalizeNameForDB(nome);
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['patients_local'], 'readonly');
    const store = transaction.objectStore('patients_local');
    const index = store.index('nome_normalizado');
    const request = index.getAll(nomeNormalizado);

    request.onsuccess = () => {
      const matches = request.result.filter(p => 
        p.nome_normalizado === nomeNormalizado && p.data_nascimento === dataNascimento
      );
      resolve({
        has_homonym: matches.length > 0,
        matches: matches
      });
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// Buscar pacientes pendentes de sync
export async function getPendingSyncPatients() {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['patients_local'], 'readonly');
    const store = transaction.objectStore('patients_local');
    const index = store.index('sync_status');
    
    const results = [];
    const localOnly = index.openCursor(IDBKeyRange.only('local_only'));
    const pending = index.openCursor(IDBKeyRange.only('pending_sync'));
    
    let completed = 0;
    
    const handleCursor = (cursor) => {
      if (cursor) {
        results.push(cursor.value);
        cursor.continue();
      } else {
        completed++;
        if (completed >= 2) {
          resolve(results);
        }
      }
    };
    
    localOnly.onsuccess = (e) => handleCursor(e.target.result);
    pending.onsuccess = (e) => handleCursor(e.target.result);
    
    localOnly.onerror = reject;
    pending.onerror = reject;
  });
}

// ==================== PRESCRIPTIONS ====================

// Criar receita local (rascunho)
export async function createPrescriptionLocal(prescriptionData) {
  const database = await getDB();
  
  const prescription = {
    local_id: generateLocalId(),
    server_id: null,
    patient_local_id: prescriptionData.patient_local_id,
    patient_server_id: prescriptionData.patient_server_id || null,
    medicamentos: prescriptionData.medicamentos || [],
    uso: prescriptionData.uso || 'USO ORAL',
    medication_groups: prescriptionData.medication_groups || [],
    data_emissao: new Date().toISOString().split('T')[0],
    data_validade: null, // Será definido quando finalizar
    status: 'draft', // draft ou final_pending
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    sync_status: 'local_only',
    version: 1
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['prescriptions_local'], 'readwrite');
    const store = transaction.objectStore('prescriptions_local');
    const request = store.add(prescription);

    request.onsuccess = () => {
      console.log('[IndexedDB] Prescription draft created:', prescription.local_id);
      // Adicionar à fila de sync para sincronizar quando reconectar
      addToSyncQueue('prescription', prescription.local_id, 'create', prescription);
      resolve(prescription);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// Atualizar receita local
export async function updatePrescriptionLocal(localId, updateData) {
  const database = await getDB();

  return new Promise(async (resolve, reject) => {
    const transaction = database.transaction(['prescriptions_local'], 'readwrite');
    const store = transaction.objectStore('prescriptions_local');
    
    const getRequest = store.get(localId);
    
    getRequest.onsuccess = () => {
      const prescription = getRequest.result;
      if (!prescription) {
        reject(new Error('Receita não encontrada'));
        return;
      }

      const updated = {
        ...prescription,
        ...updateData,
        updated_at: new Date().toISOString(),
        version: prescription.version + 1
      };

      const putRequest = store.put(updated);
      
      putRequest.onsuccess = () => {
        console.log('[IndexedDB] Prescription updated:', localId);
        resolve(updated);
      };

      putRequest.onerror = () => {
        reject(putRequest.error);
      };
    };

    getRequest.onerror = () => {
      reject(getRequest.error);
    };
  });
}

// Buscar receitas por paciente
export async function getPrescriptionsByPatient(patientLocalId) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['prescriptions_local'], 'readonly');
    const store = transaction.objectStore('prescriptions_local');
    const index = store.index('patient_local_id');
    const request = index.getAll(patientLocalId);

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// ==================== SYNC QUEUE ====================

// Adicionar à fila de sincronização
export async function addToSyncQueue(entityType, entityLocalId, action, payload) {
  const database = await getDB();
  
  const queueItem = {
    entity_type: entityType,
    entity_local_id: entityLocalId,
    action: action,
    payload: payload,
    created_at: new Date().toISOString(),
    retry_count: 0,
    last_error: null
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['sync_queue'], 'readwrite');
    const store = transaction.objectStore('sync_queue');
    const request = store.add(queueItem);

    request.onsuccess = () => {
      console.log('[IndexedDB] Added to sync queue:', entityType, action);
      resolve(queueItem);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// Obter itens da fila de sync
export async function getSyncQueue() {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['sync_queue'], 'readonly');
    const store = transaction.objectStore('sync_queue');
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// Remover item da fila de sync
export async function removeFromSyncQueue(id) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['sync_queue'], 'readwrite');
    const store = transaction.objectStore('sync_queue');
    const request = store.delete(id);

    request.onsuccess = () => {
      console.log('[IndexedDB] Removed from sync queue:', id);
      resolve();
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// Atualizar item na fila (ex: incrementar retry_count)
export async function updateSyncQueueItem(id, updates) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['sync_queue'], 'readwrite');
    const store = transaction.objectStore('sync_queue');
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const item = getRequest.result;
      if (!item) {
        reject(new Error('Item not found in sync queue'));
        return;
      }

      const updated = { ...item, ...updates };
      const putRequest = store.put(updated);

      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
}

// ==================== CACHED DATA ====================

// Salvar dados cacheados do servidor
export async function cacheServerData(key, type, data) {
  const database = await getDB();
  
  const cacheItem = {
    key: key,
    type: type,
    data: data,
    cached_at: new Date().toISOString()
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['cached_data'], 'readwrite');
    const store = transaction.objectStore('cached_data');
    const request = store.put(cacheItem);

    request.onsuccess = () => {
      console.log('[IndexedDB] Data cached:', key);
      resolve(cacheItem);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// Obter dados cacheados
export async function getCachedData(key) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['cached_data'], 'readonly');
    const store = transaction.objectStore('cached_data');
    const request = store.get(key);

    request.onsuccess = () => {
      resolve(request.result?.data);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

// ==================== APP STATE ====================

// Salvar estado do app
export async function saveAppState(key, value) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['app_state'], 'readwrite');
    const store = transaction.objectStore('app_state');
    const request = store.put({ key, value, updated_at: new Date().toISOString() });

    request.onsuccess = () => resolve(value);
    request.onerror = () => reject(request.error);
  });
}

// Obter estado do app
export async function getAppState(key) {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['app_state'], 'readonly');
    const store = transaction.objectStore('app_state');
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result?.value);
    request.onerror = () => reject(request.error);
  });
}

// ==================== CLEANUP ====================

// Limpar dados locais (logout ou expiração)
export async function clearAllLocalData() {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const stores = ['patients_local', 'prescriptions_local', 'sync_queue', 'cached_data', 'app_state'];
    let completed = 0;

    stores.forEach(storeName => {
      const transaction = database.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => {
        completed++;
        if (completed === stores.length) {
          console.log('[IndexedDB] All local data cleared');
          resolve();
        }
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  });
}

// Limpar dados expirados (mais de 15 dias)
export async function clearExpiredData() {
  const database = await getDB();
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() - 15); // 15 dias
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['patients_local', 'prescriptions_local'], 'readwrite');
    
    // Limpar pacientes expirados não sincronizados
    const patientsStore = transaction.objectStore('patients_local');
    const patientsIndex = patientsStore.index('created_at');
    const patientsCursor = patientsIndex.openCursor();
    
    patientsCursor.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const patient = cursor.value;
        if (patient.sync_status === 'local_only' && new Date(patient.created_at) < expirationDate) {
          cursor.delete();
          console.log('[IndexedDB] Deleted expired patient:', patient.local_id);
        }
        cursor.continue();
      }
    };

    transaction.oncomplete = () => {
      console.log('[IndexedDB] Expired data cleanup completed');
      resolve();
    };

    transaction.onerror = () => {
      reject(transaction.error);
    };
  });
}

// Obter estatísticas do banco local
export async function getLocalDBStats() {
  const database = await getDB();
  
  const stats = {
    patients_total: 0,
    patients_pending: 0,
    prescriptions_total: 0,
    prescriptions_draft: 0,
    sync_queue_size: 0
  };

  const patients = await getAllPatientsLocal();
  stats.patients_total = patients.length;
  stats.patients_pending = patients.filter(p => p.sync_status !== 'synced').length;

  // Contar receitas locais
  const prescriptions = await getAllPrescriptionsLocal();
  stats.prescriptions_total = prescriptions.length;
  stats.prescriptions_draft = prescriptions.filter(p => p.status === 'draft' || p.sync_status === 'local_only').length;

  const queue = await getSyncQueue();
  stats.sync_queue_size = queue.length;

  return stats;
}

// Buscar todas as receitas locais
export async function getAllPrescriptionsLocal() {
  const database = await getDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['prescriptions_local'], 'readonly');
    const store = transaction.objectStore('prescriptions_local');
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export default {
  initDB,
  createPatientLocal,
  updatePatientLocal,
  getPatientLocal,
  getAllPatientsLocal,
  checkHomonymLocal,
  getPendingSyncPatients,
  createPrescriptionLocal,
  updatePrescriptionLocal,
  getPrescriptionsByPatient,
  getAllPrescriptionsLocal,
  addToSyncQueue,
  getSyncQueue,
  removeFromSyncQueue,
  updateSyncQueueItem,
  cacheServerData,
  getCachedData,
  saveAppState,
  getAppState,
  clearAllLocalData,
  clearExpiredData,
  getLocalDBStats,
  normalizeNameForDB,
  generateLocalId
};
